import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import { EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import usePermission from "@util/usePermission";
import AssessmentViewerLinksPanel from "@components/Organization/AssessmentViewerLinksPanel";

const VARIETY_FILTER_OPTIONS = [
  { value: "1", label: "ClearScreen" },
  { value: "4", label: "Legacy ClearScreen" },
  { value: "2", label: "360" },
  { value: "3", label: "Engagement" },
];

function personLabel(person, fallbackId) {
  if (!person) return fallbackId ? String(fallbackId) : "—";
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  if (name && person.email) return `${name} (${person.email})`;
  return name || person.email || (fallbackId ? String(fallbackId) : "—");
}

function instrumentLabel(instrument, fallbackId) {
  if (!instrument) return fallbackId ? String(fallbackId) : "—";
  const variety = instrument.variety_formatted
    ? ` (${instrument.variety_formatted})`
    : "";
  return `${instrument.name || fallbackId || "—"}${variety}`;
}

export default function PlatformAssignmentRequests() {
  const { t } = useTranslation();
  const [scope, setScope] = useState("waiting_platform");
  const [variety, setVariety] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const canSeePage = usePermission(PERMISSIONS.ASSESSMENTS_APPROVE);
  const canPlatformDecide = canSeePage;

  useEffect(() => {
    if (!canSeePage) return;
    fetchData();
  }, [scope, variety, canSeePage]);

  function fetchData() {
    setLoading(true);
    const body = {
      page: 0,
      page_size: 100,
      scope,
    };
    if (variety) body.variety = Number(variety);

    axios
      .post(`/retrieve-assessment-assignment-requests/`, body)
      .then(({ data }) => {
        setRequests(data.response[0]?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function decide(requestId, approve) {
    setBusyId(requestId);
    axios
      .post(`/assessment-assignment-requests/${requestId}/decision/`, {
        approve,
      })
      .then(() => {
        setBusyId(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  if (!canSeePage) {
    return (
      <EmptyMessage
        title={t("no_permission")}
        text={t("no_permission_description")}
      />
    );
  }

  return (
    <Stack gap="md">
      <Title order={4}>{t("assignment_request_plural")}</Title>
      <Group align="flex-end" grow>
        <Select
          label={t("variety")}
          data={VARIETY_FILTER_OPTIONS}
          value={variety}
          onChange={setVariety}
          clearable
          placeholder={t("all")}
        />
      </Group>
      <SegmentedControl
        value={scope}
        onChange={setScope}
        data={[
          { label: t("entire"), value: "entire" },
          { label: t("waiting_platform"), value: "waiting_platform" },
          { label: t("waiting_org"), value: "waiting_org" },
          { label: t("approved"), value: "approved" },
          { label: t("declined"), value: "declined" },
          { label: t("withdrawn"), value: "withdrawn" },
        ]}
      />
      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <Stack gap="xs">
          {!requests.length && <EmptyMessage />}
          {requests.map((request) => {
            const waitingPlatform = request.workflow_status === 1;
            const waiting =
              request.workflow_status === 0 || request.workflow_status === 1;
            const showDecide = waitingPlatform && canPlatformDecide;
            const orgName =
              request.organization?.name || request.organization_id;

            return (
              <Card key={request.id}>
                <Flex gap="sm" align="flex-start">
                  <div style={{ flexGrow: 1 }}>
                    <Group gap="xs" mb={4}>
                      <Text fw={700}>
                        {personLabel(request.user, request.user_id)}
                      </Text>
                      <Badge size="sm" variant="light">
                        {request.workflow_status_formatted ||
                          request.status_formatted}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {t("cs:organization_singular")}:{" "}
                      <Anchor
                        component={Link}
                        to={`/organizations/${request.organization_id}`}
                        size="sm"
                      >
                        {orgName}
                      </Anchor>
                    </Text>
                    <Text size="sm" c="dimmed">
                      {t("assessment_instrument_singular")}:{" "}
                      {instrumentLabel(
                        request.assessment_instrument,
                        request.assessment_instrument_id
                      )}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {t("requested_by")}:{" "}
                      {personLabel(
                        request.requested_by,
                        request.requested_by_id
                      )}
                    </Text>
                    {(waiting || request.assessment_id) && (
                      <AssessmentViewerLinksPanel
                        organizationId={request.organization_id}
                        assessmentAssignmentRequestId={
                          waiting ? request.id : null
                        }
                        assessmentId={
                          !waiting && request.assessment_id
                            ? request.assessment_id
                            : null
                        }
                        assigneeUserId={request.user_id}
                        allowMutate={waitingPlatform}
                      />
                    )}
                  </div>
                  {showDecide && (
                    <Flex gap="xs">
                      <Button
                        size="xs"
                        color="green"
                        loading={busyId === request.id}
                        onClick={() => decide(request.id, true)}
                      >
                        {t("approve")}
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        loading={busyId === request.id}
                        onClick={() => decide(request.id, false)}
                      >
                        {t("decline")}
                      </Button>
                    </Flex>
                  )}
                </Flex>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
